// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { combineEngineMetricResults, labelEngineMetricResult } from "./utils.js";
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  sensitivityModes,
} from "../core/config.js";
import {
  normalizeSensitivity,
} from "../core/preferences.js";
import {
  cylinderKeys,
  cylinderNumberFromKey,
} from "../core/schema.js";
import {
  engineCylinderKeys,
  projectFlightDataForEngine,
} from "../core/engines.js";
import {
  clampScore,
  peerAverage,
  median,
  rankedQuantile,
  thermalEngineWorkPoint as engineWorkPoint,
} from "./utils.js";
import {
  egtBalanceParameters,
} from "./detectors/egt-balance.js";

export const egtExpectedRangeF = egtBalanceParameters.expectedRangeF;
export const egtAbsoluteReviewThresholdF = egtBalanceParameters.absoluteReviewThresholdF;
export const egtAbsoluteHighThresholdF = egtBalanceParameters.absoluteHighThresholdF;

export function cylinderEgtBehaviorRawScore(peerP90, sensorSuspicion = 0) {
  return clampScore(100
    - Math.max(0, (peerP90 ?? 0) - 70) * 0.32
    - Math.min(45, (sensorSuspicion ?? 0) * 0.8));
}

export function sensitivityAdjustedCylinderEgtBehaviorScore(score, sensitivity = "balanced") {
  if (!Number.isFinite(score)) return score;
  const penalty = 100 - score;
  const scale = sensitivityModes[normalizeSensitivity(sensitivity)].penaltyScale;
  const adjusted = clampScore(100 - penalty * scale);
  if (score <= 8 && normalizeSensitivity(sensitivity) !== "sensitive") return Math.min(adjusted, 68);
  return adjusted;
}

export function cylinderEgtBehaviorScore(peerP90, sensorSuspicion = 0, sensitivity = "balanced") {
  return sensitivityAdjustedCylinderEgtBehaviorScore(cylinderEgtBehaviorRawScore(peerP90, sensorSuspicion), sensitivity);
}

function neutralSensorSummary() {
  return {
    suspicion: 0,
    impossibleJumpCount: 0,
    outOfRangeCount: 0,
    zeroCount: 0,
    coldMismatchRatio: 0,
  };
}

function egtLabelForKey(key, options = {}) {
  return options.labelForKey?.(key) ?? key.toUpperCase();
}

function sensorSummaryForKey(rows, key, keys, options = {}) {
  return options.summarizeSensorChannel?.(rows, key, keys) ?? neutralSensorSummary();
}

function repeatedSamples(samples) {
  if (samples.length >= egtBalanceParameters.repeatedSampleMinCount) return true;
  if (samples.length < 2) return false;
  const start = samples[0]?.elapsedSeconds;
  const end = samples[samples.length - 1]?.elapsedSeconds;
  return Number.isFinite(start) && Number.isFinite(end) && end - start >= egtBalanceParameters.repeatedSampleMinSeconds;
}

function hotEgtScore(summary) {
  const config = egtBalanceParameters.hotScore;
  if (!summary?.isReview) return 100;
  if (summary.highRepeated) {
    return clampScore(config.highBaseScore
      - Math.max(0, summary.max - egtBalanceParameters.absoluteHighThresholdF) * config.highExcessPenalty
      - Math.min(config.highCountPenaltyMax, summary.highCount * config.highCountPenalty));
  }
  return clampScore(config.reviewBaseScore
    - Math.max(0, summary.max - egtBalanceParameters.absoluteReviewThresholdF) * config.reviewExcessPenalty
    - Math.min(config.reviewCountPenaltyMax, summary.reviewCount));
}

function hotEgtSummary(rows, keys, options = {}) {
  const summaries = keys.map((key) => {
    const samples = rows
      .map((point, index) => ({
        key,
        value: point?.[key],
        cylinder: cylinderNumberFromKey(key),
        elapsedSeconds: point?.elapsedSeconds ?? index,
        phase: point?.phase,
      }))
      .filter((sample) => Number.isFinite(sample.value))
      .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
    if (!samples.length) return null;
    const peak = samples.reduce((best, sample) => sample.value > best.value ? sample : best, samples[0]);
    const elevatedSamples = samples.filter((sample) => sample.value > egtBalanceParameters.expectedRangeF.max);
    const reviewSamples = samples.filter((sample) => sample.value >= egtBalanceParameters.absoluteReviewThresholdF);
    const highSamples = samples.filter((sample) => sample.value >= egtBalanceParameters.absoluteHighThresholdF);
    const elevatedRepeated = repeatedSamples(elevatedSamples);
    const reviewRepeated = repeatedSamples(reviewSamples);
    const highRepeated = repeatedSamples(highSamples);
    const summary = {
      key,
      label: egtLabelForKey(key, options),
      cylinder: cylinderNumberFromKey(key),
      max: peak.value,
      maxTime: peak.elapsedSeconds,
      phase: peak.phase,
      elevatedCount: elevatedSamples.length,
      elevatedRepeated,
      reviewCount: reviewSamples.length,
      highCount: highSamples.length,
      reviewRepeated,
      highRepeated,
      isReview: reviewRepeated || highRepeated,
      expectedMaxF: egtBalanceParameters.expectedRangeF.max,
      reviewThresholdF: egtBalanceParameters.absoluteReviewThresholdF,
      highThresholdF: egtBalanceParameters.absoluteHighThresholdF,
    };
    return {
      ...summary,
      score: hotEgtScore(summary),
    };
  }).filter(Boolean);
  const reviewSummaries = summaries.filter((summary) => summary.reviewCount > 0);
  if (!reviewSummaries.length) return null;
  const selected = reviewSummaries
    .sort((a, b) => a.score - b.score || b.max - a.max || b.highCount - a.highCount)[0] ?? null;
  if (!selected) return null;
  const channelSort = (a, b) => b.max - a.max || b.highCount - a.highCount || b.elevatedCount - a.elevatedCount;
  const elevatedChannels = summaries
    .filter((summary) => summary.elevatedRepeated || summary.reviewCount > 0 || summary.highCount > 0)
    .sort(channelSort)
    .map((summary) => ({ ...summary }));
  const reviewChannels = summaries
    .filter((summary) => summary.isReview)
    .sort(channelSort)
    .map((summary) => ({ ...summary }));
  return {
    ...selected,
    elevatedChannels,
    reviewChannels,
  };
}

function fuelFlowEgtContext(rows, keys, fuelFlowOnRow) {
  const config = egtBalanceParameters.fuelFlowContext;
  const resolveFuelFlow = typeof fuelFlowOnRow === "function" ? fuelFlowOnRow : (point) => point?.fuelFlow;
  const samples = rows
    .map((point, index) => {
      const fuelFlow = resolveFuelFlow(point);
      if (!Number.isFinite(fuelFlow)) return null;
      const egtAverage = averageValues(keys.map((key) => point?.[key]));
      if (!Number.isFinite(egtAverage)) return null;
      return {
        fuelFlow,
        egtAverage,
        elapsedSeconds: point.elapsedSeconds ?? index,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
  if (samples.length < config.minSamples) return null;

  const fuelFlows = samples.map((sample) => sample.fuelFlow);
  const egtAverages = samples.map((sample) => sample.egtAverage);
  const fuelFlowRange = extentFromValues(fuelFlows);
  const egtAverageRange = extentFromValues(egtAverages);
  if (!fuelFlowRange || !egtAverageRange) return null;

  const first = samples[0];
  const last = samples[samples.length - 1];
  const fuelFlowDelta = last.fuelFlow - first.fuelFlow;
  const egtAverageDelta = last.egtAverage - first.egtAverage;
  const fuelFlowSpan = fuelFlowRange.max - fuelFlowRange.min;
  const egtAverageSpan = egtAverageRange.max - egtAverageRange.min;
  const fuelFlowSteps = samples.slice(1).map((sample, index) => sample.fuelFlow - samples[index].fuelFlow);
  const movingSteps = fuelFlowSteps.filter((step) => Math.abs(step) >= config.minFuelFlowStepGph);
  const sameDirectionSteps = movingSteps.filter((step) => Math.sign(step) === Math.sign(fuelFlowDelta));
  const monotonicity = movingSteps.length ? sameDirectionSteps.length / movingSteps.length : 0;
  const fuelFlowMoving = fuelFlowSpan >= config.minFuelFlowSpanGph
    && Math.abs(fuelFlowDelta) >= config.minFuelFlowDeltaGph
    && monotonicity >= config.minMonotonicity;
  const egtMovingWithFuel = Math.abs(egtAverageDelta) >= config.minEgtAverageDeltaF || egtAverageSpan >= config.minEgtAverageSpanF;
  const transientLikely = fuelFlowMoving && egtMovingWithFuel;

  return {
    sampleCount: samples.length,
    fuelFlowSpan,
    fuelFlowDelta,
    egtAverageSpan,
    egtAverageDelta,
    monotonicity,
    transientLikely,
  };
}

function scoreWithFuelFlowContext(score, p90, max, fuelFlowContext) {
  const config = egtBalanceParameters.fuelFlowContext;
  if (!fuelFlowContext?.transientLikely || !Number.isFinite(score)) return score;
  const severity = Math.max(0, Math.min(1, (p90 - config.severityP90BaseF) / config.severityP90SpanF, (max - config.severityMaxBaseF) / config.severityMaxSpanF));
  const relief = Math.round((1 - severity) * config.maxRelief);
  return clampScore(score + relief);
}

function roundedDetail(value, digits = 1) {
  if (!Number.isFinite(value)) return null;
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

function phaseLooksLowPower(phase) {
  return /\b(?:idle|taxi|startup|start(?:up)?|shutdown|shut\s*down|warmup|warm-up)\b/i.test(String(phase ?? ""));
}

function lowPowerEvidenceForSpreadSample(sample) {
  const config = egtBalanceParameters.idleContext;
  const rpm = sample?.rpm;
  const fuelFlow = sample?.fuelFlow;
  const map = sample?.map;
  const percentPower = sample?.percentPower;
  const directChecks = [
    Number.isFinite(rpm) ? rpm <= config.maxLowPowerRpm : null,
    Number.isFinite(fuelFlow) ? fuelFlow <= config.maxLowPowerFuelFlowGph : null,
    Number.isFinite(map) ? map <= config.maxLowPowerMapInHg : null,
    Number.isFinite(percentPower) ? percentPower <= config.maxLowPowerPercentPower : null,
  ].filter((value) => value !== null);
  const loadedSignals = [
    Number.isFinite(rpm) && rpm >= config.loadedRpm,
    Number.isFinite(fuelFlow) && fuelFlow >= config.loadedFuelFlowGph,
    Number.isFinite(map) && map >= config.loadedMapInHg,
    Number.isFinite(percentPower) && percentPower >= config.loadedPercentPower,
  ].some(Boolean);
  const lowChecks = directChecks.filter(Boolean).length;
  const phaseLowPower = phaseLooksLowPower(sample?.phase);
  const directLowPower = directChecks.length
    ? lowChecks >= Math.ceil(directChecks.length / 2)
    : false;
  return {
    lowPower: !loadedSignals && (directLowPower || (!directChecks.length && phaseLowPower) || (phaseLowPower && lowChecks > 0)),
    phaseLowPower,
    loadedSignals,
    powerSignalCount: directChecks.length,
  };
}

function lowPowerEgtSpreadContext(spreadSamples, p90, peak) {
  const config = egtBalanceParameters.idleContext;
  const samples = spreadSamples.map((sample) => ({
    ...sample,
    ...lowPowerEvidenceForSpreadSample(sample),
  }));
  const eligible = samples.filter((sample) => sample.powerSignalCount > 0 || sample.phaseLowPower);
  if (eligible.length < config.minSamples) return null;

  const lowPowerSamples = eligible.filter((sample) => sample.lowPower);
  if (!lowPowerSamples.length) return null;
  const reviewThreshold = Math.max(
    config.minReviewSpreadF,
    Math.min(egtBalanceParameters.spread.p90ReviewF, Number.isFinite(p90) ? p90 : egtBalanceParameters.spread.p90ReviewF),
  );
  const reviewSamples = eligible.filter((sample) => Number.isFinite(sample.spread) && sample.spread >= reviewThreshold);
  const reviewLowPowerSamples = reviewSamples.filter((sample) => sample.lowPower);
  const peakLowPower = peak ? lowPowerEvidenceForSpreadSample(peak).lowPower === true : false;
  const lowPowerShare = lowPowerSamples.length / eligible.length;
  const reviewLowPowerShare = reviewSamples.length ? reviewLowPowerSamples.length / reviewSamples.length : lowPowerShare;
  const dominantLowPower = lowPowerShare >= config.minLowPowerShare
    && (reviewLowPowerShare >= config.minReviewLowPowerShare || peakLowPower);
  const active = dominantLowPower || peakLowPower;
  if (!active) return null;

  return {
    active,
    sampleCount: eligible.length,
    lowPowerSampleCount: lowPowerSamples.length,
    lowPowerShare: roundedDetail(lowPowerShare, 2),
    reviewSampleCount: reviewSamples.length,
    reviewLowPowerShare: roundedDetail(reviewLowPowerShare, 2),
    reviewSpreadThresholdF: roundedDetail(reviewThreshold, 0),
    dominantLowPower,
    peakLowPower,
    p90PenaltyScale: dominantLowPower ? config.p90PenaltyScale : 1,
    maxPenaltyScale: peakLowPower || dominantLowPower ? config.maxPenaltyScale : 1,
    rpmMedian: roundedDetail(rankedQuantile(eligible.map((sample) => sample.rpm), 0.5), 0),
    fuelFlowMedian: roundedDetail(rankedQuantile(eligible.map((sample) => sample.fuelFlow), 0.5), 1),
    mapMedian: roundedDetail(rankedQuantile(eligible.map((sample) => sample.map), 0.5), 1),
    percentPowerMedian: roundedDetail(rankedQuantile(eligible.map((sample) => sample.percentPower), 0.5), 0),
  };
}

function phaseLooksLoadedPower(phase) {
  return /\b(?:cruise|climb|takeoff|take\s*off|departure|enroute|en-route)\b/i.test(String(phase ?? ""));
}

function phaseLooksTransitionOrDescent(phase) {
  return /\b(?:descent|descend|approach|landing|flare|idle|taxi|startup|start(?:up)?|shutdown|shut\s*down|warmup|warm-up)\b/i.test(String(phase ?? ""));
}

function loadedPowerEvidenceForSpreadSample(sample) {
  const config = egtBalanceParameters.loadedContext;
  const lowPower = lowPowerEvidenceForSpreadSample(sample);
  const loadedSignals = [
    Number.isFinite(sample?.rpm) ? sample.rpm >= config.minLoadedRpm : null,
    Number.isFinite(sample?.fuelFlow) ? sample.fuelFlow >= config.minLoadedFuelFlowGph : null,
    Number.isFinite(sample?.map) ? sample.map >= config.minLoadedMapInHg : null,
    Number.isFinite(sample?.percentPower) ? sample.percentPower >= config.minLoadedPercentPower : null,
  ].filter((value) => value !== null);
  const loadedSignalCount = loadedSignals.filter(Boolean).length;
  const hasPowerSignals = loadedSignals.length > 0;
  const phaseLoaded = phaseLooksLoadedPower(sample?.phase);
  const phaseExcluded = phaseLooksTransitionOrDescent(sample?.phase);
  return {
    loadedPower: !lowPower.lowPower
      && !phaseExcluded
      && (loadedSignalCount > 0 || (!hasPowerSignals && phaseLoaded)),
    loadedSignalCount,
    hasPowerSignals,
    phaseLoaded,
    phaseExcluded,
  };
}

function stablePowerStep(current, previous, next) {
  const config = egtBalanceParameters.loadedContext;
  const channels = [
    ["rpm", config.maxStableRpmStep],
    ["fuelFlow", config.maxStableFuelFlowStepGph],
    ["map", config.maxStableMapStepInHg],
    ["percentPower", config.maxStablePercentPowerStep],
  ];
  const checks = [];
  channels.forEach(([key, limit]) => {
    const value = current?.[key];
    if (!Number.isFinite(value)) return;
    const deltas = [previous?.[key], next?.[key]]
      .filter(Number.isFinite)
      .map((other) => Math.abs(value - other));
    if (!deltas.length) return;
    checks.push(Math.max(...deltas) <= limit);
  });
  return !checks.length || checks.every(Boolean);
}

function loadedEgtSpreadSampleContext(spreadSamples) {
  const config = egtBalanceParameters.loadedContext;
  const samples = spreadSamples.map((sample, index) => {
    const loaded = loadedPowerEvidenceForSpreadSample(sample);
    return {
      ...sample,
      ...loaded,
      stablePower: stablePowerStep(sample, spreadSamples[index - 1], spreadSamples[index + 1]),
    };
  });
  const loadedSamples = samples.filter((sample) => sample.loadedPower && sample.stablePower);
  const loadedPowerSamples = samples.filter((sample) => sample.loadedPower);
  const sufficient = loadedSamples.length >= config.minSamples
    && loadedSamples.length / Math.max(1, samples.length) >= config.minSampleShare;
  return {
    active: sufficient,
    mode: sufficient ? "loaded-cruise" : "engine-run-fallback",
    sampleCount: samples.length,
    loadedSampleCount: loadedSamples.length,
    loadedSampleShare: roundedDetail(loadedSamples.length / Math.max(1, samples.length), 2),
    minSamples: config.minSamples,
    loadedPowerSamples,
    selectedSamples: sufficient ? loadedSamples : spreadSamples,
  };
}

function spreadScoreFor(p90, max, idleContext = null) {
  const spread = egtBalanceParameters.spread;
  const p90PenaltyScale = idleContext?.p90PenaltyScale ?? 1;
  const maxPenaltyScale = idleContext?.maxPenaltyScale ?? 1;
  const p90Penalty = Math.max(0, Math.min(p90, spread.p90ReviewF) - spread.p90CleanF) * spread.p90PreReviewPenalty
    + Math.max(0, p90 - spread.p90ReviewF) * spread.p90Penalty;
  return clampScore(100
    - p90Penalty * p90PenaltyScale
    - Math.max(0, max - spread.maxReviewF) * spread.maxPenalty * maxPenaltyScale);
}

function publicSpreadSample(sample) {
  if (!sample) return null;
  return {
    spread: sample.spread,
    elapsedSeconds: sample.elapsedSeconds,
    phase: sample.phase,
    high: sample.high,
    low: sample.low,
  };
}

export function dominantEgtSpreadContributor(rows, keys, options = {}) {
  const summaries = keys.map((key) => {
    const cylinder = cylinderNumberFromKey(key);
    const deviations = [];
    const coldDeviations = [];
    const hotDeviations = [];
    rows.forEach((point) => {
      const value = point[key];
      const peer = peerAverage(point, keys, key);
      if (!Number.isFinite(value) || !Number.isFinite(peer)) return;
      const delta = value - peer;
      deviations.push(Math.abs(delta));
      if (delta < 0) coldDeviations.push(Math.abs(delta));
      else hotDeviations.push(delta);
    });
    const sensorSummary = sensorSummaryForKey(rows, key, keys, options);
    const chtKey = `cht${cylinder}`;
    const chtKeys = cylinderKeys("cht").filter((candidate) => rows.some((point) => Number.isFinite(point[candidate])));
    const chtValues = rows.map((point) => point[chtKey]).filter(Number.isFinite);
    const chtPeerDeltas = rows.map((point) => {
      const value = point[chtKey];
      const peer = peerAverage(point, chtKeys, chtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? Math.abs(value - peer) : null;
    }).filter(Number.isFinite);
    const chtP95 = rankedQuantile(chtValues, 0.95);
    const chtMax = extentFromValues(chtValues)?.max ?? null;
    const chtPeerP90 = rankedQuantile(chtPeerDeltas, 0.9);
    const sameChtNormal = Number.isFinite(chtP95)
      && chtP95 < egtBalanceParameters.contributor.sameChtP95MaxF
      && (!Number.isFinite(chtMax) || chtMax < egtBalanceParameters.contributor.sameChtMaxF)
      && (!Number.isFinite(chtPeerP90) || chtPeerP90 < egtBalanceParameters.contributor.sameChtPeerP90MaxF);
    return {
      key,
      label: egtLabelForKey(key, options),
      cylinder,
      p90Deviation: rankedQuantile(deviations, 0.9) ?? 0,
      maxDeviation: extentFromValues(deviations)?.max ?? 0,
      p90ColdDeviation: rankedQuantile(coldDeviations, 0.9) ?? 0,
      p90HotDeviation: rankedQuantile(hotDeviations, 0.9) ?? 0,
      sensorSummary,
      chtKey,
      chtP95,
      chtMax,
      chtPeerP90,
      sameChtNormal,
    };
  }).filter((summary) => Number.isFinite(summary.p90Deviation));
  const top = summaries.sort((a, b) => b.p90Deviation - a.p90Deviation || b.maxDeviation - a.maxDeviation)[0] ?? null;
  if (!top) return null;
  const sensorIssue = top.sensorSummary.suspicion >= egtBalanceParameters.contributor.sensorSuspicionThreshold
    || top.sensorSummary.impossibleJumpCount >= egtBalanceParameters.contributor.impossibleJumpThreshold
    || top.sensorSummary.outOfRangeCount > 0
    || top.sensorSummary.zeroCount > 0
    || top.sensorSummary.coldMismatchRatio >= egtBalanceParameters.contributor.coldMismatchRatioThreshold;
  const strongSensorIssue = top.sensorSummary.impossibleJumpCount >= egtBalanceParameters.contributor.strongImpossibleJumpThreshold
    || top.sensorSummary.outOfRangeCount > 0
    || top.sensorSummary.zeroCount > 0;
  return {
    ...top,
    likelyProbeIssue: sensorIssue && (top.sameChtNormal || strongSensorIssue),
    sensorIssue,
    strongSensorIssue,
  };
}

function cylinderEgtBehaviorSummaries(rows, keys, options = {}) {
  return keys.map((key) => {
    const cylinder = cylinderNumberFromKey(key);
    const deviations = [];
    rows.forEach((point) => {
      const value = point[key];
      const peer = peerAverage(point, keys, key);
      if (!Number.isFinite(value) || !Number.isFinite(peer)) return;
      deviations.push(Math.abs(value - peer));
    });
    const peerP90 = rankedQuantile(deviations, 0.9) ?? null;
    const sensorSummary = sensorSummaryForKey(rows, key, keys, options);
    const sensorSuspicion = sensorSummary?.suspicion ?? 0;
    return {
      key,
      label: egtLabelForKey(key, options),
      cylinder,
      peerP90,
      maxDeviation: extentFromValues(deviations)?.max ?? null,
      sensorSuspicion,
      sensorSummary,
      score: Number.isFinite(peerP90) ? cylinderEgtBehaviorRawScore(peerP90, sensorSuspicion) : null,
    };
  }).filter((summary) => Number.isFinite(summary.score));
}

function medianSampleStepSeconds(rows) {
  const steps = rows
    .slice(1)
    .map((row, index) => row.elapsedSeconds - rows[index].elapsedSeconds)
    .filter((step) => Number.isFinite(step) && step > 0);
  return median(steps) ?? 1;
}

function longestSustainedLowRun(samples, maxGapSeconds) {
  let best = [];
  let current = [];
  samples.forEach((sample) => {
    const previous = current.at(-1);
    if (previous && sample.elapsedSeconds - previous.elapsedSeconds > maxGapSeconds) {
      if (current.length > best.length) best = current;
      current = [];
    }
    current.push(sample);
  });
  if (current.length > best.length) best = current;
  return best;
}

function contiguousRuns(samples, maxGapSeconds) {
  const runs = [];
  let current = [];
  samples.forEach((sample) => {
    const previous = current.at(-1);
    if (previous && sample.elapsedSeconds - previous.elapsedSeconds > maxGapSeconds) {
      if (current.length) runs.push(current);
      current = [];
    }
    current.push(sample);
  });
  if (current.length) runs.push(current);
  return runs;
}

function sustainedLowReviewWindow(scoreSamples, run, maxGapSeconds, config, reviewSamples = scoreSamples) {
  const samples = Array.isArray(reviewSamples) && reviewSamples.length ? reviewSamples : scoreSamples;
  const runStartTime = run[0]?.elapsedSeconds;
  const runEndTime = run.at(-1)?.elapsedSeconds;
  const runStartIndex = samples.findIndex((sample) => sample.elapsedSeconds >= runStartTime);
  const runEndIndex = (() => {
    for (let index = samples.length - 1; index >= 0; index -= 1) {
      if (samples[index].elapsedSeconds <= runEndTime) return index;
    }
    return -1;
  })();
  if (runStartIndex < 0 || runEndIndex < 0) {
    return { startTime: run[0]?.elapsedSeconds, endTime: run.at(-1)?.elapsedSeconds, sampleCount: run.length };
  }
  const qualifies = (sample) => Number.isFinite(sample?.deviationF)
    && sample.deviationF >= config.reviewDeviationF;
  let startIndex = runStartIndex;
  while (startIndex > 0) {
    const previous = samples[startIndex - 1];
    const current = samples[startIndex];
    if (!qualifies(previous) || current.elapsedSeconds - previous.elapsedSeconds > maxGapSeconds) break;
    startIndex -= 1;
  }
  let endIndex = runEndIndex;
  while (endIndex < samples.length - 1) {
    const current = samples[endIndex];
    const next = samples[endIndex + 1];
    if (!qualifies(next) || next.elapsedSeconds - current.elapsedSeconds > maxGapSeconds) break;
    endIndex += 1;
  }
  const stepSeconds = medianSampleStepSeconds(samples);
  const lookbackSeconds = Number.isFinite(config.reviewLookbackSeconds) ? config.reviewLookbackSeconds : 0;
  const leadInMinSeconds = Number.isFinite(config.reviewLeadInMinSeconds) ? config.reviewLeadInMinSeconds : 0;
  const lookbackStartTime = runStartTime - lookbackSeconds;
  const leadInRuns = contiguousRuns(samples.filter(qualifies), maxGapSeconds)
    .map((leadRun) => ({
      run: leadRun,
      startTime: leadRun[0]?.elapsedSeconds,
      endTime: leadRun.at(-1)?.elapsedSeconds,
      durationSeconds: Number.isFinite(leadRun[0]?.elapsedSeconds) && Number.isFinite(leadRun.at(-1)?.elapsedSeconds)
        ? leadRun.at(-1).elapsedSeconds - leadRun[0].elapsedSeconds + stepSeconds
        : leadRun.length * stepSeconds,
    }))
    .filter((entry) =>
      Number.isFinite(entry.startTime)
      && Number.isFinite(entry.endTime)
      && entry.durationSeconds >= leadInMinSeconds
      && entry.endTime >= lookbackStartTime
      && entry.startTime <= runStartTime);
  if (leadInRuns.length) {
    startIndex = Math.min(startIndex, samples.indexOf(leadInRuns.sort((a, b) => a.startTime - b.startTime)[0].run[0]));
  }
  return {
    startTime: samples[startIndex]?.elapsedSeconds,
    endTime: samples[endIndex]?.elapsedSeconds,
    sampleCount: Math.max(0, endIndex - startIndex + 1),
  };
}

function sustainedLowSamplesForKey(rows, keys, key, config, requireCoherentPeers = true) {
  return rows.map((point) => {
    const value = point[key];
    const peerValues = keys.filter((peerKey) => peerKey !== key).map((peerKey) => point[peerKey]).filter(Number.isFinite);
    const peer = median(peerValues);
    const peerExtent = extentFromValues(peerValues);
    const peerSpread = peerExtent ? peerExtent.max - peerExtent.min : null;
    const supportCount = peerValues.filter((peerValue) => peerValue - value >= config.moderateDeviationF).length;
    const requiredSupportCount = Math.min(config.minPeerSupportCount, peerValues.length);
    const supportShare = supportCount / Math.max(1, peerValues.length);
    if (
      !Number.isFinite(value)
      || !Number.isFinite(peer)
      || !Number.isFinite(peerSpread)
      || (requireCoherentPeers && peerSpread > config.maxPeerSpreadF)
    ) return null;
    return {
      key,
      value,
      peer,
      peerSpread,
      supportCount,
      requiredSupportCount,
      supportShare,
      deviationF: peer - value,
      elapsedSeconds: point.elapsedSeconds,
      phase: point.phase,
    };
  }).filter((sample) => Number.isFinite(sample?.deviationF));
}

function sustainedLowEgtSummary(rows, keys, options = {}) {
  const config = egtBalanceParameters.sustainedLow;
  if (!Array.isArray(rows) || rows.length < Math.max(config.minRunSamples, egtBalanceParameters.loadedContext.minSamples)) return null;
  const stepSeconds = medianSampleStepSeconds(rows);
  const maxGapSeconds = Math.max(stepSeconds * 2.5, stepSeconds + 1);
  const requireCoherentPeers = options.requireCoherentPeers !== false;
  const candidates = keys.map((key) => {
    const cylinder = cylinderNumberFromKey(key);
    const samples = sustainedLowSamplesForKey(rows, keys, key, config, requireCoherentPeers);
    if (samples.length < config.minRunSamples) return null;
    const reviewSamples = Array.isArray(options.reviewRows)
      ? sustainedLowSamplesForKey(options.reviewRows, keys, key, config, requireCoherentPeers)
      : samples;
    const lowSamples = samples.filter((sample) =>
      sample.deviationF >= config.moderateDeviationF
      && sample.supportCount >= sample.requiredSupportCount
      && sample.supportShare >= config.minPeerSupportShare);
    const run = longestSustainedLowRun(lowSamples, maxGapSeconds);
    if (run.length < config.minRunSamples) return null;
    const startTime = run[0].elapsedSeconds;
    const endTime = run.at(-1).elapsedSeconds;
    const durationSeconds = Number.isFinite(startTime) && Number.isFinite(endTime)
      ? Math.max(0, endTime - startTime + stepSeconds)
      : run.length * stepSeconds;
    const sampleShare = run.length / Math.max(1, samples.length);
    const p90DeviationF = rankedQuantile(run.map((sample) => sample.deviationF), 0.9) ?? 0;
    const maxDeviationF = extentFromValues(run.map((sample) => sample.deviationF))?.max ?? p90DeviationF;
    if (durationSeconds < config.minDurationSeconds || sampleShare < config.minSampleShare) return null;
    const reviewWindow = sustainedLowReviewWindow(samples, run, maxGapSeconds, config, reviewSamples);
    const thirds = Math.max(config.minRunSamples, Math.floor(samples.length / 3));
    const firstThird = samples.slice(0, thirds);
    const lastTwoThirds = samples.slice(thirds);
    const earlyDeviation = median(firstThird.map((sample) => sample.deviationF));
    const laterDeviation = rankedQuantile(lastTwoThirds.map((sample) => sample.deviationF), 0.75);
    const stepDropF = Number.isFinite(earlyDeviation) && Number.isFinite(laterDeviation) ? laterDeviation - earlyDeviation : null;
    const stepDown = Number.isFinite(stepDropF) && stepDropF >= config.stepDropF;
    const strong = (p90DeviationF >= config.strongDeviationF
      && (durationSeconds >= config.strongDurationSeconds || sampleShare >= 0.55 || stepDown))
      || (p90DeviationF >= 100 && durationSeconds >= config.strongDurationSeconds);
    const moderate = p90DeviationF >= config.moderateDeviationF;
    const scoreBeforeCueCap = clampScore(100
      - Math.max(0, p90DeviationF - config.moderateDeviationF) * (strong ? 0.24 : 0.16)
      - Math.max(0, durationSeconds - config.minDurationSeconds) * (strong ? 0.035 : 0.02)
      - (stepDown ? 8 : 0));
    const score = Math.min(scoreBeforeCueCap, strong ? config.strongMaxScore : config.moderateMaxScore);
    return {
      key,
      label: egtLabelForKey(key, options),
      cylinder,
      startTime,
      endTime,
      reviewStartTime: Number.isFinite(reviewWindow.startTime) ? reviewWindow.startTime : startTime,
      reviewEndTime: Number.isFinite(reviewWindow.endTime) ? reviewWindow.endTime : endTime,
      durationSeconds: roundedDetail(durationSeconds, 0),
      sampleShare: roundedDetail(sampleShare, 2),
      runSamples: run.length,
      reviewSamples: reviewWindow.sampleCount,
      stableSamples: samples.length,
      p90DeviationF: roundedDetail(p90DeviationF, 0),
      maxDeviationF: roundedDetail(maxDeviationF, 0),
      maxPeerSpreadF: roundedDetail(extentFromValues(run.map(sample => sample.peerSpread))?.max, 1),
      stepDown,
      stepDropF: roundedDetail(stepDropF, 0),
      strength: strong ? "strong" : moderate ? "moderate" : "weak",
      score,
    };
  }).filter(Boolean);
  return candidates
    .filter((candidate) => candidate.strength !== "weak")
    .sort((a, b) =>
      (!requireCoherentPeers ? a.score - b.score : 0)
      || (b.strength === "strong" ? 1 : 0) - (a.strength === "strong" ? 1 : 0)
      || b.p90DeviationF - a.p90DeviationF
      || b.durationSeconds - a.durationSeconds)[0] ?? null;
}

function analyzeEgtBalanceForChannels(data, keys, options = {}) {
  const workData = data.filter(engineWorkPoint);
  const rows = workData.length >= egtBalanceParameters.minEngineRunRows ? workData : data;
  const hot = hotEgtSummary(rows, keys, options);
  const spreadSamples = rows.map((point, index) => {
    const values = keys.map((key) => ({
      key,
      value: point[key],
      cylinder: cylinderNumberFromKey(key),
    })).filter((item) => Number.isFinite(item.value));
    if (values.length < 2) return null;
    const low = values.reduce((best, item) => item.value < best.value ? item : best, values[0]);
    const high = values.reduce((best, item) => item.value > best.value ? item : best, values[0]);
    return {
      ...point,
      spread: high.value - low.value,
      elapsedSeconds: point.elapsedSeconds ?? index,
      phase: point.phase,
      rpm: point.rpm,
      fuelFlow: point.fuelFlow,
      map: point.map,
      percentPower: point.percentPower,
      high,
      low,
    };
  }).filter(Boolean);
  const sampleContext = loadedEgtSpreadSampleContext(spreadSamples);
  const scoringSamples = sampleContext.selectedSamples.map((sample) => ({
    ...sample,
    context: sampleContext.active ? "loaded-cruise" : "engine-run-fallback",
  }));
  const spreads = scoringSamples.map((sample) => sample.spread).filter(Number.isFinite);
  if (!spreads.length) {
    if (hot) {
      const label = hot.label ?? hot.key?.toUpperCase?.() ?? "EGT";
      const elevatedCount = hot.elevatedChannels?.length ?? 0;
      const threshold = Math.round(hot.highRepeated ? hot.highThresholdF : hot.reviewThresholdF);
      const note = hot.isReview
        ? elevatedCount > 1
          ? `${elevatedCount} EGT channels exceeded expected range; ${label} peak ${Math.round(hot.max)} F`
          : `${label} exceeded ${threshold} F; peak ${Math.round(hot.max)} F`
        : `${label} isolated high EGT sample; peak ${Math.round(hot.max)} F`;
      return {
        key: "egt",
        score: hot.score,
        note,
        details: { hot, usedEngineRunRows: workData.length >= egtBalanceParameters.minEngineRunRows },
      };
    }
    return { key: "egt", score: null, note: "No EGT spread data", details: {} };
  }
  const p90 = rankedQuantile(spreads, 0.9) ?? 0;
  const max = extentFromValues(spreads)?.max ?? 0;
  const peak = scoringSamples.reduce((best, sample) => sample.spread > best.spread ? sample : best, scoringSamples[0]) ?? null;
  const idleContext = sampleContext.active ? null : lowPowerEgtSpreadContext(spreadSamples, p90, peak);
  const spreadScore = spreadScoreFor(p90, max, idleContext);
  const rawScore = Math.min(spreadScore, hot?.score ?? 100);
  const fuelFlowContext = fuelFlowEgtContext(rows, keys, options.fuelFlowOnRow);
  const scoreBeforeCylinderBehavior = scoreWithFuelFlowContext(rawScore, p90, max, fuelFlowContext);
  const cylinderBehaviorSummaries = cylinderEgtBehaviorSummaries(scoringSamples, keys, options);
  const worstCylinderBehavior = cylinderBehaviorSummaries
    .slice()
    .sort((a, b) => a.score - b.score || (b.peerP90 ?? 0) - (a.peerP90 ?? 0))[0] ?? null;
  const sustainedLowEgt = sampleContext.active && !fuelFlowContext?.transientLikely
    ? sustainedLowEgtSummary(scoringSamples, keys, {
      ...options,
      requireCoherentPeers: true,
      reviewRows: sampleContext.loadedPowerSamples,
    })
    : null;
  // Peer coherence qualifies an isolated-low interpretation, not the measured separation.
  const sustainedEgtSeparation = sampleContext.active && !fuelFlowContext?.transientLikely
    ? sustainedLowEgtSummary(scoringSamples, keys, { ...options, requireCoherentPeers: false })
    : null;
  const cylinderBehaviorApplies = sampleContext.active
    && !idleContext?.active
    && !fuelFlowContext?.transientLikely
    && Number.isFinite(worstCylinderBehavior?.score);
  const scoreAfterCylinderBehavior = cylinderBehaviorApplies
    ? Math.min(scoreBeforeCylinderBehavior, worstCylinderBehavior.score)
    : scoreBeforeCylinderBehavior;
  const score = Math.min(scoreAfterCylinderBehavior, sustainedLowEgt?.score ?? 100, sustainedEgtSeparation?.score ?? 100);
  const spreadNote = idleContext?.dominantLowPower
    ? `Idle/low-power EGT spread: ${Math.round(p90)} F between cylinders`
    : idleContext?.peakLowPower
      ? `Idle/low-power EGT peak gap: ${Math.round(max)} F`
      : p90 >= egtBalanceParameters.spread.largeDifferenceF
        ? `Large EGT difference: ${Math.round(p90)} F between cylinders`
        : `EGT difference ${Math.round(p90)} F between cylinders`;
  const cylinderBehaviorNote = cylinderBehaviorApplies
    && worstCylinderBehavior.score < scoreBeforeCylinderBehavior
    && Number.isFinite(worstCylinderBehavior.peerP90)
    ? `${worstCylinderBehavior.label} differed from peers by ${Math.round(worstCylinderBehavior.peerP90)} F`
    : "";
  const sustainedLowNote = sustainedLowEgt
    ? `${sustainedLowEgt.strength === "strong" ? "Sustained EGT drop" : "Sustained low EGT"} on Cyl ${sustainedLowEgt.cylinder}`
    : "";
  const elevatedCount = hot?.elevatedChannels?.length ?? 0;
  const note = hot?.isReview
    ? elevatedCount > 1
      ? `${elevatedCount} EGT channels exceeded expected range; ${hot.label} peak ${Math.round(hot.max)} F`
      : `${hot.label} exceeded ${Math.round(hot.highRepeated ? hot.highThresholdF : hot.reviewThresholdF)} F; peak ${Math.round(hot.max)} F`
    : sustainedLowNote || cylinderBehaviorNote || spreadNote;
  const dominant = dominantEgtSpreadContributor(scoringSamples, keys, options);
  const allSpreads = spreadSamples.map((sample) => sample.spread).filter(Number.isFinite);
  const allP90 = rankedQuantile(allSpreads, 0.9) ?? null;
  const allMax = extentFromValues(allSpreads)?.max ?? null;
  const details = {
    p90,
    max,
    dominant,
    peak: publicSpreadSample(peak),
    hot,
    usedEngineRunRows: workData.length >= egtBalanceParameters.minEngineRunRows,
    spreadSampleContext: {
      mode: sampleContext.mode,
      active: sampleContext.active,
      sampleCount: sampleContext.sampleCount,
      scoringSampleCount: scoringSamples.length,
      loadedSampleCount: sampleContext.loadedSampleCount,
      loadedSampleShare: sampleContext.loadedSampleShare,
      allP90,
      allMax,
    },
    cylinderBehavior: {
      applied: cylinderBehaviorApplies && worstCylinderBehavior?.score < scoreBeforeCylinderBehavior,
      worst: worstCylinderBehavior,
      summaries: cylinderBehaviorSummaries,
      scoreBeforeCylinderBehavior,
    },
  };
  if (sustainedLowEgt) details.sustainedLowEgt = sustainedLowEgt;
  if (sustainedEgtSeparation) details.sustainedEgtSeparation = sustainedEgtSeparation;
  if (idleContext) details.idleContext = idleContext;
  if (fuelFlowContext) details.fuelFlowContext = fuelFlowContext;
  return { key: "egt", score, note, details };
}

function composeTwinEgtNote(perEngine) {
  const segments = perEngine.map((entry) => {
    if (entry.details?.hot?.isReview) return `${entry.shortLabel} ${entry.note}`;
    const p90 = entry.details?.p90;
    if (!Number.isFinite(p90)) return null;
    const context = entry.details?.idleContext?.active ? " idle/low-power" : "";
    return `${entry.shortLabel} ${Math.round(p90)} F${context}`;
  }).filter(Boolean);
  if (!segments.length) return "No EGT spread data";

  const spreadEngines = perEngine.filter((entry) =>
    !entry.details?.hot?.isReview && Number.isFinite(entry.details?.p90));
  const largeEngines = spreadEngines.filter((entry) =>
    entry.details.p90 >= egtBalanceParameters.spread.largeDifferenceF);

  let result = segments.join("; ");
  if (spreadEngines.length) result += " between cylinders";
  if (largeEngines.length === 1) result += ` — Large EGT difference (${largeEngines[0].shortLabel})`;
  else if (largeEngines.length >= 2) result += " — Large EGT difference";
  return result;
}

export function analyzeEgtBalance(data, options = {}) {
  const descriptors = Array.isArray(options.engineDescriptors) ? options.engineDescriptors : null;
  if (descriptors && descriptors.length > 1) {
    const perEngine = descriptors.map((engine) => {
      const projected = projectFlightDataForEngine(data, engine);
      const engineKeys = engineCylinderKeys(engine, "egt")
        .filter((key) => projected.some((point) => Number.isFinite(point[key])));
      const result = labelEngineMetricResult(analyzeEgtBalanceForChannels(projected, engineKeys, options), descriptors);
      return {
        engineKey: engine.engineKey ?? engine.id ?? `engine${(engine.index ?? 0) + 1}`,
        label: engine.label,
        shortLabel: engine.shortLabel ?? engine.label,
        ...result,
      };
    }).filter(Boolean);
    if (perEngine.filter((entry) => Number.isFinite(entry.score)).length < 2) {
      return combineEngineMetricResults(perEngine.map((entry) => ({ ...entry, result: entry })));
    }
    if (perEngine.length >= 2) {
      const worst = perEngine.reduce((acc, entry) => {
        if (acc === null) return entry;
        const accScore = Number.isFinite(acc.score) ? acc.score : 100;
        const entryScore = Number.isFinite(entry.score) ? entry.score : 100;
        if (entryScore < accScore) return entry;
        if (entryScore === accScore && (entry.details?.p90 ?? 0) > (acc.details?.p90 ?? 0)) return entry;
        return acc;
      }, null);
      const score = perEngine.reduce(
        (acc, entry) => Math.min(acc, Number.isFinite(entry.score) ? entry.score : 100),
        100,
      );
      const note = composeTwinEgtNote(perEngine);
      const details = {
        ...worst.details,
        engines: perEngine.map((entry) => ({
          engineKey: entry.engineKey,
          label: entry.label,
          shortLabel: entry.shortLabel,
          score: entry.score,
          note: entry.note,
          details: entry.details,
        })),
        worstEngineKey: worst.engineKey,
      };
      return { key: "egt", score, note, details };
    }
    if (perEngine.length === 1) {
      const only = perEngine[0];
      return { key: "egt", score: only.score, note: only.note, details: only.details };
    }
  }
  const keys = cylinderKeys("egt").filter((key) => data.some((point) => Number.isFinite(point[key])));
  return analyzeEgtBalanceForChannels(data, keys, options);
}
